import React, { useEffect, useRef, useState } from 'react';
import { Row, Col } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import apiOffers from 'api/offers';
import IOfferProductsGet from 'api/interfaces/offers/IOfferProductsGet';
import maximize from 'assets/maximize.svg';
import minimize from 'assets/minimize.svg';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import ModalConfirm from 'components/layout/modals/ModalConfirm';
import { moveItemWithinSubset } from 'services/arrays/moveItem';
import translations from 'services/translations/translations';
import { RIGHT } from 'services/enums/sides';
import { addMessage } from 'store/messages/actions';
import { fetchInstallationCategories } from 'store/products/actions';
import {
  convertMoney,
  currencyOfCountry,
  currencySymbol,
  MoneyCurrency,
  useExchangeRate,
} from 'services/offers/exchangeRate';

import CurrencyToggle from '../CurrencyToggle';

import { isPositionInUse } from '../offerProduct/usedPositions';
import OfferPdfPriceChoices from './OfferPdfPriceChoices';
import OfferProductsTable from './OfferProductsTable';
import RestorePositionModal from './RestorePositionModal';
import SearchPositionModal from './SearchPositionModal';
import OfferWorkTime from './OfferWorkTime';
import OfferLastActivity from './OfferLastActivity';

// A failed load of the positions is tried this many times in all, waiting longer each time
const LOAD_ATTEMPTS = 3;
const LOAD_RETRY_DELAY_MS = 1500;

const OfferProducts = ({
  country,
  locked,
  ndaPositionsRestricted,
  offerId,
  offerSubmitted,
  productsIsOpen,
  toggleProducts,
  type,
  products,
  setProducts,
}: {
  // The country of the department of the offer: its prices are in SEK in Sweden, else €
  country?: null | string;
  locked: boolean;
  // The positions are NDA protected and the user is outside the NDA
  ndaPositionsRestricted: boolean;
  offerId: number | undefined;
  offerSubmitted: boolean;
  productsIsOpen: boolean;
  toggleProducts: () => void;
  type: string;
  products: null | IOfferProductsGet[];
  setProducts: (products: IOfferProductsGet[]) => void;
}): JSX.Element | null => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers, translations.products]);

  const [restoreIsOpen, setRestoreIsOpen] = useState(false);
  // The position whose deletion is being confirmed
  const [deleting, setDeleting] = useState<IOfferProductsGet | null>(null);
  const [searchIsOpen, setSearchIsOpen] = useState(false);
  const [reordering, setReordering] = useState(false);
  // The server refused the positions (NDA)
  const [forbidden, setForbidden] = useState(false);

  // The positions can be viewed in the other currency at the rate of today; only what is
  // shown changes
  const baseCurrency = currencyOfCountry(country);
  const [shownCurrency, setShownCurrency] =
    useState<MoneyCurrency>(baseCurrency);
  useEffect(() => setShownCurrency(baseCurrency), [baseCurrency]);
  const exchangeRate = useExchangeRate(productsIsOpen && offerSubmitted);
  const money = {
    convert: (value: number) =>
      convertMoney(value, baseCurrency, shownCurrency, exchangeRate),
    symbol: currencySymbol(shownCurrency),
  };

  // Needed to count only installations (not other expenses) in the position hours
  useEffect(() => {
    dispatch(fetchInstallationCategories());
  }, [dispatch]);

  // A position deleted from the summary once confirmed, with its calculation
  const deletePosition = async () => {
    if (deleting === null) {
      return;
    }

    const { id } = deleting;
    setDeleting(null);
    try {
      await apiOffers.products.delete(id);
      dispatch(addMessage(t(`${translations.offers}:Position deleted`)));
    } catch (error) {
      dispatch(
        addMessage(t(`${translations.offers}:positionErrors.Delete failed`))
      );
    }
    loadProducts();
  };

  // The positions could not be loaded even when tried again; shown with a button to retry
  const [loadFailed, setLoadFailed] = useState(false);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  // A failed load (e.g. the database busy while the page loads) is tried again a couple
  // of times before it is shown, instead of leaving the positions empty until a refresh
  const loadProducts = async (attempt = 1): Promise<void> => {
    if (offerId === undefined) {
      return;
    }

    try {
      const loaded =
        type === 'additional-offer'
          ? await apiOffers.products.getForAdditionalOffer(offerId)
          : await apiOffers.products.getForOffer(offerId);
      // E.g. a page of a proxy or a sign-in instead of the positions
      if (!Array.isArray(loaded)) {
        throw new Error('The positions were not a list');
      }
      if (mounted.current) {
        setLoadFailed(false);
        setProducts(loaded);
      }
    } catch (error) {
      if (!mounted.current) {
        return;
      }

      if (error?.response?.status === 403) {
        setForbidden(true);
        setProducts([]);
        return;
      }

      if (attempt < LOAD_ATTEMPTS) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, attempt * LOAD_RETRY_DELAY_MS);
        });
        await loadProducts(attempt + 1);
        return;
      }

      setLoadFailed(true);
    }
  };

  // Loaded when the section is open and the positions are not loaded yet, also when the
  // offer becomes known only after the section was opened
  useEffect(() => {
    if (!productsIsOpen || products !== null || offerId === undefined) {
      return;
    }

    loadProducts();
  }, [productsIsOpen, offerId, products === null]);

  const addProduct = () => {
    history.push(`/offers/${offerId}/${type}/products/create`);
  };

  const offerIsLocked = () => locked;

  // The rows of the additional price list are in a section of their own
  const positions =
    products === null
      ? null
      : products.filter((product) => product.isPriceListItem !== true);

  // Moves a position within its work type and saves the order at once, as the order
  // is shown in the PDF; on an error the saved order is loaded back
  const movePosition = async (
    group: string,
    fromIndex: number,
    toIndex: number
  ) => {
    if (products === null || offerId === undefined) {
      return;
    }

    const reordered = moveItemWithinSubset(
      products,
      (product) => product.group === group && product.isPriceListItem !== true,
      fromIndex,
      toIndex
    );
    setProducts(reordered);

    try {
      const ids = reordered
        .filter((product) => product.isPriceListItem !== true)
        .map((product) => product.id);
      if (type === 'additional-offer') {
        await apiOffers.additionalOffers.positionOrder.put(offerId, ids);
      } else {
        await apiOffers.positionOrder.put(offerId, ids);
      }
    } catch (error) {
      dispatch(
        addMessage(t(`${translations.offers}:Saving position order failed`))
      );
      loadProducts();
    }
  };

  // Changes the code of a position (e.g. VS1) in the summary; only the code is saved. A
  // code another position of the offer already has is not taken.
  const changePositionCode = async (id: number, position: string) => {
    const otherCodes = (positions ?? [])
      .filter((product) => product.id !== id)
      .map((product) => product.position);

    if (isPositionInUse(position, otherCodes)) {
      dispatch(
        addMessage(t(`${translations.offers}:Position in use`, { position }))
      );
      return false;
    }

    try {
      await apiOffers.products.patchPosition(id, position);
      if (products !== null) {
        setProducts(
          products.map((product) =>
            product.id === id ? { ...product, position } : product
          )
        );
      }
      return true;
    } catch (error) {
      dispatch(
        addMessage(t(`${translations.offers}:Saving position code failed`))
      );
      return false;
    }
  };

  const canReorder =
    !offerIsLocked() && positions !== null && positions.length > 1;

  // Who looked at and saved the offer last, also for those who can only view it
  const lastActivity =
    offerId === undefined ? null : (
      <OfferLastActivity
        isAdditionalOffer={type === 'additional-offer'}
        offerId={offerId}
      />
    );

  const getOrderToolbar = () =>
    canReorder || lastActivity !== null ? (
      <div className="position-order-toolbar">
        {reordering ? null : lastActivity}
        {reordering ? (
          <span className="position-order-info">
            {t(`${translations.offers}:Position order info`)}
          </span>
        ) : null}
        {canReorder ? (
          <PrimaryButton
            className={reordering ? 'position-order-active' : undefined}
            onClick={() => setReordering(!reordering)}
            size="sm"
          >
            {reordering
              ? t(`${translations.offers}:Position order done`)
              : t(`${translations.offers}:Change position order`)}
          </PrimaryButton>
        ) : null}
      </div>
    ) : null;

  const getActionButtons = (showPriceChoices: boolean) => (
    <Row className="offer-products-actions">
      {/* Which prices the offer PDF shows, on the left of the buttons */}
      {offerId === undefined || !showPriceChoices ? null : (
        <Col xs="auto" className="mt-3">
          <OfferPdfPriceChoices
            locked={offerIsLocked()}
            owner={type === 'additional-offer' ? 'additionaloffers' : 'offers'}
            ownerId={offerId}
          />
        </Col>
      )}
      <Col>
        {offerIsLocked() ? null : (
          <>
            <PrimaryButton
              float={RIGHT}
              className="button-one-line mt-3"
              onClick={addProduct}
            >
              {t(`${translations.products}:Add Product`).toLocaleUpperCase()}
            </PrimaryButton>
            <PrimaryButton
              float={RIGHT}
              className="button-one-line mt-3"
              onClick={() => setSearchIsOpen(true)}
            >
              {t(
                `${translations.offers}:positionSearch.Open`
              ).toLocaleUpperCase()}
            </PrimaryButton>
            <PrimaryButton
              float={RIGHT}
              className="button-one-line mt-3"
              onClick={() => setRestoreIsOpen(true)}
            >
              {t(
                `${translations.offers}:Restore position from earlier revision`
              ).toLocaleUpperCase()}
            </PrimaryButton>
          </>
        )}
      </Col>
    </Row>
  );

  const getContent = () => {
    if (!offerSubmitted || !productsIsOpen) {
      return null;
    }

    if (ndaPositionsRestricted || forbidden) {
      return (
        <p className="offer-nda-info">
          {t(`${translations.offers}:nda.Positions protected`)}
        </p>
      );
    }

    // Not loaded yet: loading, or failed with a button to try again; not an empty table,
    // which would look like an offer without positions
    if (positions === null) {
      return loadFailed ? (
        <p className="offer-products-loading">
          {t(`${translations.offers}:Loading positions failed`)}{' '}
          <PrimaryButton
            onClick={() => {
              setLoadFailed(false);
              loadProducts();
            }}
            size="sm"
          >
            {t(`${translations.offers}:Load again`)}
          </PrimaryButton>
        </p>
      ) : (
        <p className="offer-products-loading">
          {t(`${translations.offers}:Loading positions`)}
        </p>
      );
    }

    const productsTable = positions;

    return (
      <>
        {getOrderToolbar()}
        <OfferProductsTable
          money={money}
          move={movePosition}
          products={productsTable}
          // A trash can at the end of each row, the deletion confirmed in a window
          remove={
            offerIsLocked()
              ? undefined
              : (id) =>
                  setDeleting(
                    productsTable.find((product) => product.id === id) ?? null
                  )
          }
          rename={changePositionCode}
          reordering={reordering && canReorder}
          type={type}
          workTypeSummary
        />
        {getActionButtons(productsTable.length > 0)}
        <ModalConfirm
          confirm={deletePosition}
          header={t(`${translations.offers}:Delete position`)}
          noButton={t(`${translations.common}:Cancel`)}
          setShowing={(showing) => {
            if (!showing) {
              setDeleting(null);
            }
          }}
          showing={deleting !== null}
          text={t(`${translations.offers}:Delete position confirmation`, {
            name: deleting?.name ?? '',
            position: deleting?.position ?? '',
          })}
          yesButton={t(`${translations.offers}:Delete position`)}
        />
        {/* The installation time of the offer, as in the calculation window */}
        {offerId === undefined ? null : (
          <OfferWorkTime
            offerId={offerId}
            positions={productsTable}
            type={type}
          />
        )}
        {offerId === undefined || offerIsLocked() ? null : (
          <>
            <RestorePositionModal
              isOpen={restoreIsOpen}
              offerId={offerId}
              onRestored={() => loadProducts()}
              toggle={() => setRestoreIsOpen(!restoreIsOpen)}
              type={type}
            />
            <SearchPositionModal
              isOpen={searchIsOpen}
              offerId={offerId}
              onCopied={() => loadProducts()}
              toggle={() => setSearchIsOpen(!searchIsOpen)}
              type={type}
            />
          </>
        )}
      </>
    );
  };
  return (
    <>
      <Row>
        <Col>
          <div
            className="header-text title-spacing"
            onClick={toggleProducts}
            role="button"
            tabIndex={0}
          >
            {productsIsOpen ? (
              <img
                src={minimize}
                alt="toggle-products"
                className="collapse-icon"
              />
            ) : (
              <img
                src={maximize}
                alt="toggle-products"
                className="collapse-icon"
              />
            )}
            {t(`${translations.products}:Products`).toUpperCase()}
          </div>
        </Col>
        {/* The currency shown, with the exchange rate below it */}
        {offerSubmitted &&
        productsIsOpen &&
        !ndaPositionsRestricted &&
        !forbidden ? (
          <Col className="title-spacing" xs="auto">
            <CurrencyToggle
              base={baseCurrency}
              onChange={setShownCurrency}
              rate={exchangeRate}
              value={shownCurrency}
            />
          </Col>
        ) : null}
      </Row>
      {getContent()}
    </>
  );
};

export default OfferProducts;
